import { useMutation } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp"
import React, { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { v4 as uuidv4 } from "uuid"
import * as z from "zod"
import { gql } from "~/__generated__"
import { useUiAccess } from "~/auth/use-ui-access"
import { useViewerMaybe } from "~/auth/use-viewer"
import { RETURN_TO_STORAGE_KEY, useNavigateAfterAuth } from "~/auth/utils"
import { useFormErrorHandling } from "~/common/error-handling"
import { Button } from "~/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { TextField } from "~/ui/forms/fields/text-field"
import { FormGeneralErrors } from "~/ui/forms/form-general-errors"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "~/ui/input-otp"
import { EMAIL_TOKEN_AUTH_MUTATION } from "./email-auth-screen"
import { updateCsrfTag } from "./utils"

const emailLoginFormSchema = z.object({
  email: z.email({ message: "That doesn't look like a valid email." }),
})

const profileFormSchema = z.object({
  firstName: z.string().trim().min(1, { message: "Required" }),
  lastName: z.string().trim().min(1, { message: "Required" }),
})

const otpFormSchema = z.object({
  otp: z.string().length(6, {
    message: "Your one-time password must be 6 characters.",
  }),
})

type EmailLoginFormValues = z.infer<typeof emailLoginFormSchema>
type ProfileFormValues = z.infer<typeof profileFormSchema>
type OtpFormValues = z.infer<typeof otpFormSchema>

const EMAIL_AUTH_CHALLENGE_MUTATION = gql(/* GraphQL */ `
  mutation EmailAuthChallenge($input: EmailUserAuthChallengeInput!) {
    emailUserAuthChallenge(input: $input) {
      success
      needsProfile
    }
  }
`)

// Map known returnTo paths to a friendly action label so the user sees
// "Sign in to register your puzzle" instead of just landing on a blank form.
const returnToActionLabel = (path: string | null): string | null => {
  if (!path) return null
  if (path === "/puzzles/new" || path.startsWith("/puzzles/new")) return "register your puzzle"
  if (path === "/dashboard") return "open your dashboard"
  if (path.startsWith("/p/") && path.endsWith("/log")) return "log your stop"
  if (path.startsWith("/profile")) return "open your profile"
  return null
}

const buildPreviewName = (firstName: string, lastName: string): string | null => {
  const first = firstName.trim()
  if (!first) return null
  const initial = lastName.trim().charAt(0).toUpperCase()
  return initial ? `${first} ${initial}.` : first
}

export const EmailLoginScreen: React.FC = () => {
  const [emailAuthChallenge, { loading: challengeLoading }] = useMutation(
    EMAIL_AUTH_CHALLENGE_MUTATION
  )
  const [emailTokenAuth, { loading: tokenAuthLoading }] = useMutation(EMAIL_TOKEN_AUTH_MUTATION)
  const [step, setStep] = useState<"email" | "profile" | "checkEmail">("email")
  const [email, setEmail] = useState("")
  const [resending, setResending] = useState(false)
  const { navigateAfterAuth } = useNavigateAfterAuth()
  const {
    result: { refetch: viewerRefetch },
  } = useViewerMaybe()
  const { refetch: refetchUiAccess } = useUiAccess()

  const returnToAction = useMemo(
    () => returnToActionLabel(localStorage.getItem(RETURN_TO_STORAGE_KEY)),
    []
  )

  const emailForm = useForm<EmailLoginFormValues>({
    resolver: zodResolver(emailLoginFormSchema),
    defaultValues: { email: "" },
  })

  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: { firstName: "", lastName: "" },
  })

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: { otp: "" },
  })

  const watchedFirstName = profileForm.watch("firstName")
  const watchedLastName = profileForm.watch("lastName")
  const previewName = useMemo(
    () => buildPreviewName(watchedFirstName, watchedLastName),
    [watchedFirstName, watchedLastName]
  )

  const { onError: onEmailFormError } = useFormErrorHandling(emailForm)
  const { onError: onProfileFormError } = useFormErrorHandling(profileForm)
  const { onError: onOtpFormError } = useFormErrorHandling(otpForm)

  useEffect(() => {
    if (step !== "checkEmail") setResending(false)
  }, [step])

  const getOrCreateClientAuthCode = () => {
    const stored = JSON.parse(localStorage.getItem("authCodes") || "[]")
    if (stored.length === 0) {
      const fresh = uuidv4()
      localStorage.setItem("authCodes", JSON.stringify([fresh]))
      return fresh
    }
    return stored[stored.length - 1]
  }

  const onEmailSubmit = async (values: EmailLoginFormValues) => {
    const { data, error } = await emailAuthChallenge({
      onError: onEmailFormError,
      variables: {
        input: {
          email: values.email,
          clientAuthCode: getOrCreateClientAuthCode(),
        },
      },
    })

    if (error || !data) return

    setEmail(values.email)
    if (data.emailUserAuthChallenge.needsProfile) {
      setStep("profile")
      return
    }
    if (data.emailUserAuthChallenge.success) {
      setStep("checkEmail")
    } else {
      toast.error("Couldn't send the link", {
        description: "Double-check the email and try again.",
      })
    }
  }

  const onProfileSubmit = async (values: ProfileFormValues) => {
    const { data, error } = await emailAuthChallenge({
      onError: onProfileFormError,
      variables: {
        input: {
          email,
          clientAuthCode: getOrCreateClientAuthCode(),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    })

    if (error || !data) return

    if (data.emailUserAuthChallenge.success) {
      setStep("checkEmail")
    } else {
      toast.error("Couldn't create your account", {
        description: "Please try again in a moment.",
      })
    }
  }

  const onResend = async () => {
    if (!email) return
    setResending(true)
    const { data, error } = await emailAuthChallenge({
      variables: {
        input: { email, clientAuthCode: getOrCreateClientAuthCode() },
      },
    })
    setResending(false)
    if (error || !data?.emailUserAuthChallenge.success) {
      toast.error("Couldn't resend", {
        description: "Try again in a moment.",
      })
      return
    }
    toast.success("Link resent", { description: `Sent another link to ${email}.` })
  }

  const onOtpSubmit = async (values: OtpFormValues) => {
    const { data, error } = await emailTokenAuth({
      onError: onOtpFormError,
      variables: {
        input: {
          email,
          token: values.otp,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    })

    if (error || !data) return

    if (data.emailTokenUserAuth.success) {
      updateCsrfTag(data.emailTokenUserAuth.csrfToken)
      await viewerRefetch()
      await refetchUiAccess()
      toast.success("You're signed in.")
      navigateAfterAuth()
    } else {
      toast.error("That code didn't match", {
        description: "Try the link in the email, or paste the code again.",
      })
    }
  }

  const heading =
    step === "profile"
      ? "Welcome to Wandering Puzzles"
      : step === "checkEmail"
        ? "Check your email"
        : "Sign in to Wandering Puzzles"

  return (
    <div className="w-full space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="font-display text-3xl font-semibold">{heading}</h1>
        {step === "email" && returnToAction ? (
          <p className="text-sm text-stone-600">Sign in to {returnToAction}.</p>
        ) : null}
      </div>

      {step === "email" && (
        <Form {...emailForm}>
          <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-5">
            <FormGeneralErrors control={emailForm.control} />
            <TextField
              control={emailForm.control}
              name="email"
              label="Email"
              placeholder="you@example.com"
            />
            <Button type="submit" className="w-full" disabled={challengeLoading}>
              {challengeLoading ? "Sending…" : "Continue"}
            </Button>
            <p className="text-center text-sm text-stone-600">
              We&apos;ll email you a link. No password needed.
            </p>
          </form>
        </Form>
      )}

      {step === "profile" && (
        <Form {...profileForm}>
          <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-5">
            <p className="text-sm text-stone-700">
              We don&apos;t have an account for <span className="font-medium">{email}</span> yet.
              Tell us your name and we&apos;ll send the link.
            </p>
            <FormGeneralErrors control={profileForm.control} />
            <TextField
              control={profileForm.control}
              name="firstName"
              label="First name"
              placeholder="First name"
            />
            <div className="space-y-1">
              <TextField
                control={profileForm.control}
                name="lastName"
                label="Last name"
                placeholder="Last name"
              />
              <p className="text-xs text-stone-500">
                {previewName
                  ? `On other puzzlers' pages you'll appear as "${previewName}".`
                  : "Just the first letter of your last name shows publicly (e.g. “Eric S.”)."}
              </p>
            </div>
            <Button type="submit" className="w-full" disabled={challengeLoading}>
              {challengeLoading ? "Creating account…" : "Create account & send link"}
            </Button>
            <button
              type="button"
              onClick={() => setStep("email")}
              className="block w-full text-center text-sm text-stone-500 hover:text-stone-800"
            >
              ← Use a different email
            </button>
          </form>
        </Form>
      )}

      {step === "checkEmail" && (
        <div className="space-y-6">
          <p className="text-center text-sm text-stone-700">
            We sent a sign-in link to <span className="font-medium">{email}</span>. Click the link
            to sign in, or paste the 6-character code below.
          </p>
          <Form {...otpForm}>
            <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="flex flex-col space-y-5">
              <FormGeneralErrors control={otpForm.control} />
              <FormField
                control={otpForm.control}
                name="otp"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center">
                    <FormLabel className="sr-only">Enter code</FormLabel>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        {...field}
                        onChange={(value) =>
                          field.onChange(value.replace(/\s+/g, "").toUpperCase())
                        }
                        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                        data-1p-ignore="true"
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={tokenAuthLoading}>
                {tokenAuthLoading ? "Signing you in…" : "Sign me in"}
              </Button>
            </form>
          </Form>
          <div className="flex flex-col gap-2 text-center">
            <button
              type="button"
              onClick={onResend}
              disabled={resending}
              className="text-sm text-stone-600 underline-offset-2 hover:text-stone-900 hover:underline disabled:opacity-50"
            >
              {resending ? "Resending…" : "Didn't get it? Resend the link."}
            </button>
            <button
              type="button"
              onClick={() => setStep("email")}
              className="text-sm text-stone-500 hover:text-stone-800"
            >
              ← Use a different email
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
